// Zod contracts for the attachments module. Filled by M4 (C11); nothing else edits this file.
// Every request and response schema lives here (architecture/architecture.md Section 3.11).
//
//   GET  /api/attachments/:publicId/:rendition      the file, streamed after the access check
//                                                   (rendition: original | with-markup | thumbnail)
//   POST /api/tickets/:ref/attachments              staff upload (multipart part "file") -> StoredAttachment;
//                                                   pass its `id` in a `comment` command's attachmentIds
//   POST /api/my-requests/:publicId/attachments     the reporter's reply file -> StoredAttachment;
//                                                   pass its `id` in a `reporter_reply` command
import { z } from 'zod';
import { CODE_LISTS } from '../constants/code-lists.js';
import { envelope } from './common.js';

const Uuid = z.uuid();
const IsoDateTime = z.iso.datetime({ offset: true });

export const AttachmentKindSchema = z.enum(CODE_LISTS.attachment_kind);
export const AttachmentMaskingSchema = z.enum(CODE_LISTS.attachment_masking);
export const RenditionKindSchema = z.enum(CODE_LISTS.rendition_kind);

/** URL path segments for the renditions (kebab-case paths, architecture Section 11). */
export const RENDITION_PATH_SEGMENTS = {
  original: 'original',
  with_markup: 'with-markup',
  thumbnail: 'thumbnail',
} as const;
export const RenditionSegmentSchema = z.enum(['original', 'with-markup', 'thumbnail']);
export type RenditionSegment = z.infer<typeof RenditionSegmentSchema>;

export const AttachmentFileParamsSchema = z.object({
  publicId: z.string().trim().min(1).max(100),
  rendition: RenditionSegmentSchema,
});

/** The multipart field name the upload routes read. */
export const UPLOAD_FILE_FIELD = 'file';

/** The URL of one rendition, relative to the tool's origin. */
export function renditionUrl(publicId: string, rendition: keyof typeof RENDITION_PATH_SEGMENTS): string {
  return `/api/attachments/${encodeURIComponent(publicId)}/${RENDITION_PATH_SEGMENTS[rendition]}`;
}

/** What an upload answers with, and how attachments appear in the ticket detail and reporter view. */
export const AttachmentViewSchema = z.object({
  /** The internal ID: what `comment` / `reporter_reply` commands take in attachmentIds. */
  id: Uuid,
  /** In the file URLs; unguessable. */
  publicId: z.string(),
  kind: AttachmentKindSchema,
  /** `not_masked` for reporter uploads and reply files: the UI shows NOT MASKED (REQ-3.4, REQ-10.4). */
  masking: AttachmentMaskingSchema,
  /** Masked. Screenshots: the page it was grabbed on. */
  pageUrl: z.string().nullable(),
  takenAt: IsoDateTime.nullable(),
  fileName: z.string().nullable(),
  /** The original rendition's checked type and size (from the bytes, not the name). */
  contentType: z.string().nullable(),
  byteSize: z.number().int().nullable(),
  addedAt: IsoDateTime,
  /** Rendition URLs that exist. Empty once the files are removed. */
  renditions: z.object({
    original: z.string().optional(),
    withMarkup: z.string().optional(),
    thumbnail: z.string().optional(),
  }),
  /** Set when the files were removed (retention or an admin); the row stays as a tombstone (REQ-19.1). */
  removedAt: IsoDateTime.nullable(),
  removalKind: z.enum(CODE_LISTS.removal_kind).nullable(),
});
export type AttachmentView = z.infer<typeof AttachmentViewSchema>;
/** StoredAttachment: the same shape, returned by the store methods and upload routes. */
export type StoredAttachment = AttachmentView;
export const AttachmentUploadResponseSchema = envelope(AttachmentViewSchema);
